import { useState, useEffect } from "react";
import {
  watchSupportTickets,
  createSupportTicket,
  updateSupportTicket,
  watchTicketMessages,
  addTicketMessage,
} from "../lib/db";
import { TICKET_STATUS } from "../lib/constants";
import EmptyState from "../components/EmptyState";
import { LifeBuoy } from "lucide-react";

export default function Support({ contacts = [] }) {
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [reply, setReply] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [form, setForm] = useState({ subject: "", contactEmail: "", contactId: "", priority: "normal" });

  useEffect(() => {
    return watchSupportTickets(setTickets, () => {});
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    return watchTicketMessages(selectedId, setMessages, () => {});
  }, [selectedId]);

  const selected = tickets.find((t) => t.id === selectedId);

  async function submitNew(e) {
    e.preventDefault();
    const id = await createSupportTicket({
      subject: form.subject,
      contactEmail: form.contactEmail,
      contactId: form.contactId || null,
      priority: form.priority,
      source: "manual",
    });
    setForm({ subject: "", contactEmail: "", contactId: "", priority: "normal" });
    setShowNew(false);
    setSelectedId(id);
  }

  async function sendReply() {
    if (!reply.trim() || !selectedId) return;
    const text = reply.trim();
    setReplySending(true);
    try {
      await addTicketMessage(selectedId, text, "Agent", false);
      setReply("");
      await updateSupportTicket(selectedId, { status: "pending" });
    } finally {
      setReplySending(false);
    }
  }

  async function setStatus(status) {
    if (!selectedId) return;
    await updateSupportTicket(selectedId, { status });
  }

  return (
    <div className="page-wrap responsive-split">
      <div>
        <header className="page-header" style={{ marginBottom: 12 }}>
          <h1 className="page-header-title">Support</h1>
          <p className="page-header-desc">{tickets.length} ticket{tickets.length !== 1 ? "s" : ""}</p>
        </header>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowNew(true)}>
            + New
          </button>
        </div>
        <div className="card" style={{ padding: 0, maxHeight: "70vh", overflow: "auto" }}>
          {tickets.length === 0 ? (
            <EmptyState
              icon={LifeBuoy}
              title="No tickets yet"
              description="Create one for a customer issue. tawk.to webhooks and inbound email can also create tickets automatically."
            />
          ) : (
            tickets.map((t) => (
              <div
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                style={{
                  padding: "12px 14px",
                  borderBottom: "0.5px solid var(--border)",
                  cursor: "pointer",
                  background: selectedId === t.id ? "var(--bg-elevated)" : "transparent",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500 }}>{t.subject}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
                  {t.contactEmail || "—"} · <span className="badge badge-muted">{t.status}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="card" style={{ minHeight: 360 }}>
        {!selected ? (
          <div style={{ color: "var(--text-3)", padding: 24, textAlign: "center" }}>Select a ticket</div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{selected.subject}</div>
                <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
                  {selected.contactEmail} · {selected.source}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {TICKET_STATUS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`btn btn-sm ${selected.status === s.id ? "btn-primary" : ""}`}
                    onClick={() => setStatus(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                border: "0.5px solid var(--border)",
                borderRadius: 8,
                padding: 12,
                maxHeight: 280,
                overflow: "auto",
                marginBottom: 12,
                background: "var(--bg-elevated)",
              }}
            >
              {messages.length === 0 ? (
                <div style={{ color: "var(--text-3)", fontSize: 13 }}>No messages yet.</div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} style={{ marginBottom: 12, fontSize: 13 }}>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                      {m.author} · {m.createdAt?.toDate?.()?.toLocaleString?.() || ""}
                      {m.internal ? " · internal" : ""}
                    </div>
                    <div style={{ marginTop: 4, lineHeight: 1.5, color: "var(--text-2)" }}>{m.text}</div>
                  </div>
                ))
              )}
            </div>
            {selected.source === "tawk" && (
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8, lineHeight: 1.45 }}>
                Replies here are saved in the CRM only. To message the visitor, use the{" "}
                <strong>tawk.to</strong> dashboard (inbox / ticket).
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <textarea
                className="textarea"
                placeholder="Reply to customer…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                disabled={replySending}
                style={{ flex: 1, minHeight: 72, marginBottom: 0 }}
              />
              <button
                type="button"
                className="btn btn-primary"
                style={{ alignSelf: "flex-end" }}
                disabled={replySending}
                onClick={sendReply}
              >
                {replySending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        )}
      </div>

      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">New support ticket</div>
              <button type="button" className="btn btn-ghost btn-icon" onClick={() => setShowNew(false)}>
                ×
              </button>
            </div>
            <form onSubmit={submitNew} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Subject *
                <input
                  className="input"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  required
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Customer email
                <input
                  className="input"
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Link contact
                <select
                  className="input"
                  value={form.contactId}
                  onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                >
                  <option value="">— Optional —</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Priority
                <select
                  className="input"
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowNew(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
